import { Tevent } from "types/transcript"

export type MediaTypeVideo = {
  type: "video"
  url: string
  hlsUrl?: string
  thumbnail?: string
  id?: string
  offset?: string
  totalLength?: string
  taskId?: string
  title?: string
}

export type MediaTypeImage = {
  type: "image"
  url: string
  autoplay?: boolean
}

export type MediaTypeWebsite = {
  type: "website"
  url: string
  id?: string
  title?: string
  thumbnail?: string
}

export type MediaTypeTodoList = {
  type: "todoList"
}

export type MediaTypeCrisisLine = {
  type: "crisisLine"
}

export type MediaTypeRoleSwitch = {
  type: "roleSwitch"
}

export type MediaTypeAudio = {
  type: "audio"
  url: string
  title?: string
  autoplay?: boolean
  id?: string
  offset?: string
  totalLength?: string
  taskId?: string
}

export type MediaTypeResource = {
  type: "resource"
  url: string
  id: string
  resourceType: string
  title?: string
  description?: string
  autoplay?: boolean
  offset?: string
  taskId?: string
  totalLength?: string
  thumbnail?: string
}

export type MediaType =
  | MediaTypeVideo
  | MediaTypeImage
  | MediaTypeWebsite
  | MediaTypeTodoList
  | MediaTypeCrisisLine
  | MediaTypeRoleSwitch
  | MediaTypeAudio
  | MediaTypeResource

export const getMediaTypeFromEvent = (event: Tevent, apiHost: string): MediaType | undefined => {
  switch (event.event_type) {
    case "ShowTDLPanel":
      return { type: "todoList" }
    case "HideTDLPanel":
      return undefined
    case "ExpandTDLSection":
      return undefined
    case "TDLChanged":
      return undefined
    case "PlayVideo":
      // eslint-disable-next-line no-case-declarations
      const videoUrl = getUrlFromEvent(event, apiHost)
      if (!videoUrl) {
        return undefined
      }
      // eslint-disable-next-line no-case-declarations
      const hlsKey = event.event_args?.playlist_s3_key
      // eslint-disable-next-line no-case-declarations
      const hlsUrl = hlsKey ? new URL(hlsKey, `https://${apiHost}`).href : undefined
      // eslint-disable-next-line no-case-declarations
      const thumbnailKey = event.event_args?.thumbnail_url
      // eslint-disable-next-line no-case-declarations
      const thumbnail = thumbnailKey ? new URL(thumbnailKey, `https://${apiHost}`).href : undefined
      return {
        type: "video",
        url: videoUrl,
        hlsUrl,
        thumbnail,
        id: event.event_args?.id,
        offset: event.event_args?.offset,
        totalLength: event.event_args?.total_length,
        taskId: event.event_args?.task_id,
        title: event.event_args?.title,
      }
    case "HideVideoPanel":
      return undefined
    case "ShowImage":
      // eslint-disable-next-line no-case-declarations
      const imageUrl = getUrlFromEvent(event, apiHost)
      if (!imageUrl) {
        return undefined
      }
      return { type: "image", url: imageUrl, autoplay: event.event_args?.autoplay ?? false }
    case "PlayAudio":
      // eslint-disable-next-line no-case-declarations
      const audioUrl = getUrlFromEvent(event, apiHost)
      if (!audioUrl) {
        return undefined
      }
      return {
        type: "audio",
        url: audioUrl,
        title: event.event_args?.resource_title ?? event.event_args?.title,
        autoplay: event.event_args?.autoplay ?? false,
        id: event.event_args?.id,
        offset: event.event_args?.offset,
        totalLength: event.event_args?.total_length,
        taskId: event.event_args?.task_id,
      }
    case "OpenResource":
      // eslint-disable-next-line no-case-declarations
      const resourceUrl = getUrlFromEvent(event, apiHost)
      if (!resourceUrl) {
        return undefined
      }
      // eslint-disable-next-line no-case-declarations
      const resourceThumbnailKey = event.event_args?.thumbnail_url
      // eslint-disable-next-line no-case-declarations
      const resourceThumbnail = resourceThumbnailKey
        ? new URL(resourceThumbnailKey, `https://${apiHost}`).href
        : undefined
      return {
        type: "resource",
        url: resourceUrl,
        id: event.event_args?.id,
        resourceType: event.event_args?.resource_type,
        title: event.event_args?.resource_title ?? event.event_args?.title,
        description: event.event_args?.resource_description,
        autoplay: event.event_args?.autoplay ?? false,
        // Backend should send offset as a string, but guard against numeric values in the interim
        offset: event.event_args?.offset != null ? String(event.event_args.offset) : undefined,
        taskId: event.event_args?.task_id,
        totalLength: event.event_args?.total_length,
        thumbnail: resourceThumbnail,
      }
    case "ShowWebBrowserPanel":
      // eslint-disable-next-line no-case-declarations
      const websiteUrl = event.event_args?.url
      // eslint-disable-next-line no-case-declarations
      const websiteThumbnailKey = event.event_args?.thumbnail_url
      // eslint-disable-next-line no-case-declarations
      const websiteThumbnail = websiteThumbnailKey
        ? new URL(websiteThumbnailKey, `https://${apiHost}`).href
        : undefined
      return {
        type: "website",
        url: websiteUrl,
        id: event.event_args?.id,
        title: event.event_args?.resource_title ?? event.event_args?.title,
        thumbnail: websiteThumbnail,
      }
    case "HideWebBrowserPanel":
      return undefined
    case "ShowToast":
      return undefined
    case "UserPressedSmiley":
      return undefined
    case "ShowWellnessBar":
      return undefined
    case "ShowCrisisLine":
      return { type: "crisisLine" }
  }
  return undefined
}

export const getUrlFromEvent = (event: Tevent, apiHost: string): string | undefined => {
  const eventUrl = event.event_args?.url
  if (!eventUrl) {
    return undefined
  }
  if (eventUrl.startsWith("http")) {
    return eventUrl
  }
  return new URL(eventUrl, `https://${apiHost}`).href
}

/**
 * Returns true when `url` points at an HLS playlist (`.m3u8`). Tolerates
 * arbitrary query strings (e.g. CloudFront signed URLs).
 */
export const isHlsUrl = (url: string | null | undefined): boolean => {
  if (!url) return false
  return /\.m3u8(?:$|\?)/i.test(url)
}

/**
 * Best-effort extraction of a string URI from an `expo-video` `VideoSource`.
 * `VideoSource` can be a string, an object with `uri`, or a numeric asset id;
 * for our purposes we only need to recognize the first two cases (the asset
 * resolver is web-irrelevant for streamed content).
 */
export const getSourceUri = (
  source: string | { uri?: string | null } | null | undefined,
): string | null => {
  if (typeof source === "string") return source
  if (source && typeof source === "object") return source.uri ?? null
  return null
}
